import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@cmps/ng-bootstrap';
import { CmpsIconRegistry } from '@cmps/ng-uikit';

import { CmpsDebugModule } from './_debug/debug.module';
import { CmpsLazyAdminModule } from './admin/lazy.admin.module';
import { AppComponent } from './app.component';
import { AppConfigProvider } from './app.config';
import { TranslationIdMap } from './app.i18n';
import { AppInitService } from './app.init';
import { AuthHttpInterceptor } from './auth/auth.http-interceptor';
import { CmpsAuthModule } from './auth/auth.module';
import { CmpsCommitmentModule } from './coaching-commitment/coaching-commitment.module';
import { CmpsCoachingDetailModule } from './coaching-detail/coaching-detail.module';
import { CoachingCacheHttpInterceptor } from './coaching-insight/coaching-cache.http-interceptor';
import { CmpsCoachingInsightModule } from './coaching-insight/coaching-insight.module';
import { OldOaProviders } from './coaching-insight/oa-old/old-oa-providers';
import { CmpsNoteModule } from './coaching-note/notes.module';
import { CmpsObservationModule } from './coaching-observation/coaching-observation.module';
import { CmpsCoachingTagsModule } from './coaching-tags/coaching-tags.module';
import { CmpsCoachingFocusSelectorModule } from './coaching/focus-selector/focus-selector.module';
import { CmpsCoachingWinningPercentageModule } from './coaching/insight/winning-percentage';
import { CmpsCoachingNewTranscriptDialogModule } from './coaching/new-transcript-dialog';
import { CmpsCoachingTranscriptImportFocusDialogModule } from './coaching/transcript-import-focus-dialog';
import { CacheHttpInterceptor } from './common/cache.http-interceptor';
import { CmpsCommonModule } from './common/common.module';
import { CmpsEvaluationModule } from './evaluation/evaluation.module';
import { CmpsFormModule } from './form/form.module';
import { CmpsHelpModule } from './help/help.module';
import { CmpsI18nModule, I18nLocaleProvider } from './i18n/i18n.module';
import { CmpsInsightExplorerModule } from './insight-explorer/insight-explorer.module';
import { CmpsInsightNewModule } from './insight-new/insight-new.module';
import { CmpsInsightModule } from './insight/insight.module';
import { CmpsLegacyWidgetModule } from './legacy-widget/legacy-widget.module';
import { CmpsLocationModule } from './location/location.module';
import { CmpsNavModule } from './nav/nav.module';
import { CmpsNotificationModule } from './notification/notification.module';
import { CmpsPerformanceInsightModule } from './performance-insight/performance-insight.module';
import { CmpsProfileModule } from './profile/profile.module';
import { CmpsPulseModule } from './pulse/pulse.module';
import { CmpsReportsModule } from './reports/reports.module';
import { CmpsRouterModule } from './route.module';
import { CmpsScorecardWidgetModule } from './scorecard/pulse-widget/pulse-widget.module';
import { CmpsScorecardModule } from './scorecard/scorecard.module';
import { StoreProvider } from './store.metadata';
import { SystemNotifierService } from './system';
import { CmpsTdCfFormModule } from './tdcf-form/tdcf-form.module';
import { CmpsUserModule } from './user/user.module';
import { CmpsVimeoModule } from './vimeo/vimeo.module';
import { CmpsWebFormModule } from './webform/webform.module';
import { CmpsWidgetModule } from './widget/widget.module';

@NgModule({
    imports: [
        BrowserModule,
        FormsModule,
        HttpClientModule,
        CmpsI18nModule.forRoot(TranslationIdMap),
        CmpsCommonModule.forRoot(),
        CmpsRouterModule.forRoot(),
        CmpsUserModule.forRoot(),
        CmpsAuthModule.forRoot(),
        CmpsDebugModule,
        CmpsProfileModule,
        CmpsNavModule.forRoot(),
        CmpsNotificationModule.forRoot(),
        CmpsLazyAdminModule,
        CmpsLocationModule.forRoot(),
        CmpsWebFormModule,
        CmpsWidgetModule.forRoot(),
        CmpsPulseModule,
        CmpsInsightNewModule,
        CmpsPerformanceInsightModule,
        CmpsLegacyWidgetModule.forRoot(),
        CmpsVimeoModule.forRoot(),
        CmpsHelpModule,
        CmpsFormModule.forRoot(),
        CmpsTdCfFormModule.forRoot(),
        CmpsCoachingInsightModule.forRoot(),
        CmpsInsightExplorerModule,
        CmpsInsightModule.forRoot(),
        CmpsReportsModule.forRoot(),
        CmpsScorecardModule.forRoot(),
        CmpsScorecardWidgetModule,
        CmpsNoteModule,
        CmpsObservationModule.forRoot(),
        CmpsCommitmentModule.forRoot(),
        CmpsCoachingTagsModule,
        CmpsCoachingDetailModule,
        CmpsCoachingNewTranscriptDialogModule,
        CmpsEvaluationModule,
        CmpsCoachingWinningPercentageModule,
        CmpsCoachingFocusSelectorModule,
        CmpsCoachingTranscriptImportFocusDialogModule,
    ],
    providers: [
        NgbActiveModal,
        AppInitService,
        AppConfigProvider,
        I18nLocaleProvider,
        StoreProvider,
        SystemNotifierService,
        // this is temporarily here until coaching modules are consolidated
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CoachingCacheHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheHttpInterceptor,
            multi: true,
        },
        OldOaProviders,
    ],
    declarations: [AppComponent],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(domSanitizer: DomSanitizer, iconRegistry: CmpsIconRegistry) {
        iconRegistry.addSvgIconSet(
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/mdi.svg'),
        );

        // TODO register under 'coaching' namespace. maybe move to uikit
        [
            'minigoalchartup',
            'minigoalchartdown',
            'minigoalchartsteady',
            'buttonflagupdate',
            'expired-goal',
            'evaluation-check-icon',
            'draft-icon',
            'pending-icon',
            'oa-missed-opp',
        ].forEach((e) => {
            iconRegistry.addSvgIcon(
                e,
                domSanitizer.bypassSecurityTrustResourceUrl(
                    `./assets/${e}.svg`,
                ),
            );
        });

        // TODO maybe move to uikit
        ['publish'].forEach((e) => {
            iconRegistry.addSvgIcon(
                e,
                domSanitizer.bypassSecurityTrustResourceUrl(
                    `./assets/${e}.svg`,
                ),
                'coaching',
            );
        });
    }
}
